import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table';
import { useTranslation } from '@/lib/i18n';
import { useNavigate } from 'react-router';
import { useMemo } from 'react';
import type { Money, SettlementListItem } from '@/lib/api-admin/types';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui';
import { SettlementRunStatusPill } from './SettlementRunStatusPill';

export const SETTLEMENT_SORTABLE_COLUMNS = new Set([
  'createdAt',
  'periodStart',
  'netPayout',
]);

export interface SettlementRunsTableProps {
  items: ReadonlyArray<SettlementListItem>;
  sort: string;
  onSortChange: (sort: string) => void;
}

export const parseSettlementSort = (
  sort: string,
): { id: string; dir: 'asc' | 'desc' } => {
  const parts = sort.split(',');
  const id = parts[0] && parts[0].length > 0 ? parts[0] : 'createdAt';
  const dir = parts[1] === 'asc' ? 'asc' : 'desc';
  return { id, dir };
};

export const toggleSettlementSort = (current: string, columnId: string): string => {
  const { id, dir } = parseSettlementSort(current);
  if (id !== columnId) return `${columnId},desc`;
  return `${columnId},${dir === 'desc' ? 'asc' : 'desc'}`;
};

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const formatMoney = (money: Money | undefined | null): string => {
  if (!money) return '—';
  return `${new Intl.NumberFormat('ko-KR').format(money.amount)} ${money.currencyCode}`;
};

export const SettlementRunsTable = ({
  items,
  sort,
  onSortChange,
}: SettlementRunsTableProps) => {
  const { t } = useTranslation('settlement');
  const navigate = useNavigate();
  const { id: activeSortId, dir: activeSortDir } = parseSettlementSort(sort);

  const columns = useMemo<ColumnDef<SettlementListItem>[]>(
    () => [
      {
        id: 'vendor',
        accessorFn: (row) => row.vendorName ?? row.vendorId,
        header: () => t('settlement.columns.vendor'),
        cell: ({ row }) => (
          <span
            className="block max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap text-sm font-medium text-[color:var(--content-strong)]"
            title={row.original.vendorName ?? row.original.vendorId}
          >
            {row.original.vendorName ?? row.original.vendorId}
          </span>
        ),
      },
      {
        id: 'cycle',
        accessorFn: (row) => row.cycle,
        header: () => t('settlement.columns.cycle'),
        cell: ({ row }) => (
          <span className="text-sm text-[color:var(--content-default)]">
            {t(`settlement.cycle.${row.original.cycle}`, { defaultValue: '-' })}
          </span>
        ),
      },
      {
        id: 'periodStart',
        accessorFn: (row) => row.periodStart,
        header: () => t('settlement.columns.period'),
        cell: ({ row }) => (
          <div className="flex flex-col text-xs text-[color:var(--content-muted)]">
            <span>{formatDate(row.original.periodStart)}</span>
            <span>~ {formatDate(row.original.periodEnd)}</span>
          </div>
        ),
      },
      {
        id: 'netPayout',
        accessorKey: 'netPayout',
        header: () => t('settlement.columns.netPayout'),
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="text-sm font-medium text-[color:var(--content-strong)]">
              {formatMoney(row.original.netPayout)}
            </span>
            <span className="text-[10px] text-[color:var(--content-muted)]">
              gross {formatMoney(row.original.grossAmount)}
            </span>
          </div>
        ),
      },
      {
        id: 'itemCount',
        accessorKey: 'itemCount',
        header: () => t('settlement.columns.itemCount'),
      },
      {
        id: 'status',
        header: () => t('settlement.columns.status'),
        cell: ({ row }) => (
          <SettlementRunStatusPill
            status={row.original.status}
            runId={row.original.runId}
          />
        ),
      },
      {
        id: 'runType',
        accessorKey: 'runType',
        header: () => t('settlement.columns.runType'),
        cell: ({ row }) => (
          <span className="text-xs text-[color:var(--content-muted)]">
            {t(`settlement.runType.${row.original.runType}`, { defaultValue: '-' })}
          </span>
        ),
      },
      {
        id: 'createdAt',
        accessorFn: (row) => formatDate(row.createdAt),
        header: () => t('settlement.columns.createdAt'),
      },
    ],
    [t],
  );

  const table = useReactTable({
    data: items as SettlementListItem[],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (items.length === 0) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
        style={{
          background: 'var(--surface-raised)',
          color: 'var(--content-muted)',
          boxShadow: 'var(--shadow-card)',
        }}
      >
        <p>{t('settlement.list.empty')}</p>
      </div>
    );
  }

  const goToDetail = (runId: string) => navigate(`/settlements/${runId}`);

  return (
    <div
      className="overflow-hidden rounded-lg"
      style={{ background: 'var(--surface-raised)', boxShadow: 'var(--shadow-card)' }}
    >
      <Table data-testid="settlement-runs-table">
        <TableHeader style={{ background: 'var(--surface-1)' }}>
          {table.getHeaderGroups().map((group) => (
            <TableRow key={group.id}>
              {group.headers.map((header) => {
                const isSortable = SETTLEMENT_SORTABLE_COLUMNS.has(header.column.id);
                const isActive = activeSortId === header.column.id;
                const ariaSort: 'ascending' | 'descending' | 'none' = isSortable
                  ? isActive
                    ? activeSortDir === 'asc' ? 'ascending' : 'descending'
                    : 'none'
                  : 'none';
                return (
                  <TableHead
                    key={header.id}
                    scope="col"
                    aria-sort={isSortable ? ariaSort : undefined}
                  >
                    {isSortable ? (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 focus-visible:outline-none focus-visible:ring-2"
                        onClick={() => onSortChange(toggleSettlementSort(sort, header.column.id))}
                        aria-label={t('settlement.list.sortBy', {
                          column: t(`settlement.columns.${header.column.id}`),
                        })}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        <span aria-hidden="true">
                          {isActive ? (activeSortDir === 'asc' ? '↑' : '↓') : ''}
                        </span>
                      </button>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              role="link"
              tabIndex={0}
              data-testid="settlement-row"
              aria-label={t('settlement.list.openRow', { runId: row.original.runId })}
              className="cursor-pointer focus-visible:outline-none focus-visible:ring-2"
              style={{ height: 'var(--density-compact-row-h)' }}
              onClick={() => goToDetail(row.original.runId)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  goToDetail(row.original.runId);
                }
              }}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(
                    cell.column.columnDef.cell ?? ((ctx) => ctx.getValue() as string),
                    cell.getContext(),
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
